// src/main.js
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';

// Импортируем конфигурацию i18n
import i18n from './plugins/i18n';

// Импортируем директивы для управления доступом
import { vPermission, vRole } from './directives/permission';

// Импортируем Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
// Импортируем ВСЕ иконки из free-solid-svg-icons
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

// Добавляем все иконки в библиотеку
library.add(fas, fab);

// Импортируем vue3-particles
import Particles from 'vue3-particles';

// Импортируем сервис Google Analytics и плагин
import analyticsService from './services/analytics';
import analyticsPlugin from './plugins/analytics';

// Импортируем плагин проверки версии
import versionCheckPlugin from './plugins/version-check';

// Импортируем сервис геолокации
import { detectLocaleAndCountry, persistDetectedPrefs } from './services/geo';
import { migrateFromLocalStorageOnce } from '@/services/tokenStore';
import api, { refreshAccessToken } from '@/services/axios';
import { login, finishAuthInitialization, updateRole } from '@/store/auth';
import { accessState } from '@/store/access';

import '@/assets/styles/variables.css';
import { installScrollLockCompensation } from '@/utils/scrollLockCompensation';

// Окна блокируют прокрутку страницы — без компенсации контент дёргается вбок.
installScrollLockCompensation();

// Ранняя инициализация геолокации и языка (до создания приложения)
async function initializeLocaleAndGeo() {
  try {
    const savedLang = localStorage.getItem('user-language');
    const savedCountry = localStorage.getItem('user-country');

    // Если нет сохранённых настроек - определяем автоматически
    if (!savedLang || !savedCountry) {
      const { lang, countryCode } = await detectLocaleAndCountry();

      // Устанавливаем язык в i18n если он ещё не установлен
      if (!savedLang && lang) {
        localStorage.setItem('user-language', lang);
      }

      // Сохраняем страну если её ещё нет
      if (!savedCountry && countryCode) {
        localStorage.setItem('user-country', countryCode);
      }

      // Сохраняем обе настройки
      persistDetectedPrefs(
        savedLang || lang || 'ru',
        savedCountry || countryCode || ''
      );
    }
  } catch (e) {
    // Если автодетект не сработал, используем дефолтные значения
    if (!localStorage.getItem('user-language')) {
      localStorage.setItem('user-language', 'ru');
    }
  }
}

// Вызываем initTheme синхронно для предотвращения мерцания
const savedTheme = localStorage.getItem('theme');
if (savedTheme) {
  const isDark = savedTheme === 'dark';
  const html = document.documentElement;
  if (isDark) {
    html.setAttribute('data-theme', 'dark');
  } else {
    html.removeAttribute('data-theme');
  }
} else {
  // Проверяем системные настройки
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const html = document.documentElement;
  if (prefersDark) {
    html.setAttribute('data-theme', 'dark');
  }
  localStorage.setItem('theme', prefersDark ? 'dark' : 'light');
}

// Миграция токена из localStorage -> память на старте приложения
migrateFromLocalStorageOnce();

async function bootstrapAuth() {
  // Restore a QuickPrice session from its local refresh cookie.
  try {
    const accessToken = await refreshAccessToken();
    if (accessToken) {
      // Роль читается из localStorage (сохраняется при логине)
      // NavBar обновит её через userService.getUserInfo() при загрузке
      const savedRole = localStorage.getItem('user_role') || 'user';
      login(accessToken, savedRole, { finishInitialization: false });
      try {
        const { data: profile } = await api.get('/auth/get_user_profile');
        if (profile?.role) {
          updateRole(profile.role);
        }
      } catch (_) {
        // Если профиль недоступен, оставляем роль из login() и завершаем bootstrap.
      }
      try {
        await accessState.load(true);
      } catch (_) {
        // Fail closed: protected module routes will resolve to /403.
      }
      finishAuthInitialization();
      return;
    }
  } catch (e) {
    // Refresh cookie отсутствует или просрочен — остаёмся гостем.
  }

  finishAuthInitialization();
}

const app = createApp(App);

app.use(router);
app.use(i18n); // Подключаем i18n
app.use(Particles); // Используем плагин Particles
app.use(analyticsPlugin); // Используем плагин Analytics
app.use(versionCheckPlugin, { router }); // Используем плагин проверки версии

// Регистрируем глобальные директивы для управления доступом
app.directive('permission', vPermission);
app.directive('role', vRole);

app.component('font-awesome-icon', FontAwesomeIcon);

// Инициализируем геолокацию и затем монтируем приложение
// Монтируем приложение сразу после геолокации, не ждем auth
// Auth будет работать в фоне, роутер сам решит когда ждать
initializeLocaleAndGeo().then(() => {
  // Монтируем приложение сразу
  app.mount('#app');

  // Инициализируем Google Analytics
  analyticsService.init();

  // Запускаем auth в фоне (не блокируя UI)
  bootstrapAuth();
}).catch(() => {
  // В случае ошибки всё равно монтируем приложение
  app.mount('#app');
  analyticsService.init();
  bootstrapAuth();
});
